/**
 * Labs Page
 *
 * Displays lab orders filtered from the documents list.
 * Split layout: document list on left, lab order form on right.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PatientPicker from '@components/Patient/PatientPicker';
import { PdfViewer } from '@components/PdfViewer';
import { Avatar } from '@components/primitives/Avatar';
import { EntitlementGate, EntitlementUsage } from '@features/entitlements';
import { LabOrderForm } from '@features/order-labs/components/LabOrderForm';
import { useLabRecommendations } from '@features/order-labs/hooks/useLabRecommendations';
import { useCreateLabOrder, useLabOrderPdf } from '@features/order-labs/store/hooks';
import type { CreateLabOrderRequest, LabOrderRecommendation } from '@features/order-labs/types';
import { useSubjectLanguageVariants } from '@features/user';
import { usePatientById } from '@hooks/usePatientById';
import {
  ArrowBack as BackIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Science as LabIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
  type GridRowParams,
  type GridSortModel,
} from '@mui/x-data-grid';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { lazyWithRetry } from '../../../utils/lazyWithRetry';

const LabRecommendationsDiff = lazyWithRetry(() =>
  import('@features/order-labs/components/LabRecommendationsDiff').then(module => ({
    default: module.LabRecommendationsDiff,
  })),
);

import type { Patient } from '../../../types/types';
import { type SortField, useDocumentsList } from '../hooks/useDocumentsList';
import { useLazyGetDocumentProxyBlobQuery } from '../store/api';
import type { DocumentSummaryResponse } from '../types';

import { DocumentDetailModal } from './DocumentDetailModal';
import DocumentFilters from './DocumentFilters';

// Format date to localized string
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const LabsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const { singularCap: subjectLanguageCap } = useSubjectLanguageVariants();
  const {
    labOrder: labOrderMetrics,
    toolbar: toolbarMetrics,
    document: documentMetrics,
  } = useEventTracking();

  // Track page view
  const hasTrackedPageView = useRef(false);

  // Document detail modal state
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Lab order form state
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  // Mobile full-screen lab order view state
  const [isMobileLabOrderViewOpen, setIsMobileLabOrderViewOpen] = useState(false);
  const [formData, setFormData] = useState<CreateLabOrderRequest | null>(null);
  const [pdfData, setPdfData] = useState<{ filename: string; pdf_data: string } | null>(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  // AI recommendations state
  const [showLabRecommendations, setShowLabRecommendations] = useState(false);
  const [pendingRecommendations, setPendingRecommendations] =
    useState<CreateLabOrderRequest | null>(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [currentFormValues, setCurrentFormValues] = useState<Partial<CreateLabOrderRequest> | null>(
    null,
  );
  const [shouldApplyRecommendations, setShouldApplyRecommendations] = useState(false);
  const [diffAnchorEl, setDiffAnchorEl] = useState<HTMLButtonElement | null>(null);
  const mobileApplyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Lab order hooks
  const { generate: generatePdf, isLoading: isGeneratingPdf } = useLabOrderPdf();
  const { mutate: createLabOrder, isLoading: isCreatingOrder } = useCreateLabOrder();

  // Document download hook - fetch blob directly via proxy
  const [fetchDocumentBlob] = useLazyGetDocumentProxyBlobQuery();

  // Memoize the request object for lab recommendations
  const labRecommendationRequest = useMemo(
    () =>
      selectedPatient?.patient_id
        ? { patient_id: selectedPatient.patient_id, note_id: null }
        : null,
    [selectedPatient?.patient_id],
  );

  // Memoize the onSuccess callback for lab recommendations
  const handleLabRecommendationsSuccess = useCallback((response: LabOrderRecommendation) => {
    // Only show banner if we have completed status with recommendations
    if (response.status === 'completed' && response.lab_order) {
      setShowLabRecommendations(true);
      // Store the recommendations for later use
      setPendingRecommendations(response.lab_order);
    }
  }, []);

  // Lab recommendations hook - fetch when patient is selected
  const { loading: loadingRecommendations, data: labRecommendations } = useLabRecommendations(
    labRecommendationRequest,
    {
      enabled: !!selectedPatient?.patient_id && !showPdfPreview,
      onSuccess: handleLabRecommendationsSuccess,
    },
  );

  // Use the documents list hook with fixed lab_order filter
  const {
    documents,
    isLoading,
    isFetching,
    error,
    page,
    pageSize,
    totalRows,
    filters,
    sortBy,
    sortOrder,
    hasActiveFilters,
    setPage,
    setPageSize,
    setPatientId,
    setStartDate,
    setEndDate,
    setSortBy,
    setSortOrder,
    clearFilters,
    refresh,
  } = useDocumentsList({ fixedDocumentType: 'lab_order', syncWithUrl: false });

  // Fetch patient when filter patientId is set (for syncing with form picker)
  const { patient: filterPatient } = usePatientById(filters.patientId || undefined);

  // Sync filter patient to form patient when filter changes
  useEffect(() => {
    if (
      filterPatient &&
      (!selectedPatient || selectedPatient.patient_id !== filterPatient.patient_id)
    ) {
      setSelectedPatient(filterPatient);
    } else if (!filters.patientId && selectedPatient) {
      // Clear form patient when filter is cleared
      setSelectedPatient(null);
      // Reset form state when patient is cleared
      setFormData(null);
      setPdfData(null);
      setShowPdfPreview(false);
      setShowLabRecommendations(false);
      setPendingRecommendations(null);
      setIsFormDirty(false);
      setCurrentFormValues(null);
      setShouldApplyRecommendations(false);
    }
  }, [filterPatient, filters.patientId, selectedPatient]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (mobileApplyTimeoutRef.current) {
        clearTimeout(mobileApplyTimeoutRef.current);
      }
    };
  }, []);

  // Track page view once documents are loaded
  useEffect(() => {
    if (!isLoading && !hasTrackedPageView.current) {
      documentMetrics.pageViewed({
        page_type: 'labs',
        document_count: totalRows,
        has_filters: false, // Labs page has fixed filter
      });
      hasTrackedPageView.current = true;
    }
  }, [isLoading, totalRows, documentMetrics]);

  // Handle row click to open detail modal
  const handleRowClick = useCallback(
    (params: GridRowParams<DocumentSummaryResponse>) => {
      const doc = params.row;
      // Track row click
      documentMetrics.rowClicked({
        document_id: doc.document_id,
        document_type: 'lab_order',
        page_type: 'labs',
        has_patient: !!doc.patient_id,
      });
      setSelectedDocumentId(doc.document_id);
      setIsDetailModalOpen(true);
    },
    [documentMetrics],
  );

  // Handle view button click
  const handleViewClick = useCallback(
    (event: React.MouseEvent, document: DocumentSummaryResponse) => {
      event.stopPropagation();
      setSelectedDocumentId(document.document_id);
      setIsDetailModalOpen(true);
    },
    [],
  );

  // Handle download button click
  const handleDownloadClick = useCallback(
    async (event: React.MouseEvent, doc: DocumentSummaryResponse) => {
      event.stopPropagation();
      try {
        // Fetch blob directly via RTK Query proxy endpoint (auth handled automatically)
        const blob = await fetchDocumentBlob({
          documentId: doc.document_id,
          disposition: 'attachment',
        }).unwrap();

        // Track download action
        documentMetrics.downloaded({
          document_id: doc.document_id,
          document_type: 'lab_order',
          page_type: 'labs',
          file_size: doc.size_bytes,
        });

        const blobUrl = URL.createObjectURL(blob);

        // Create a temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = doc.filename || `${doc.document_title || 'document'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the blob URL after a short delay
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      } catch (error) {
        console.error('Download error:', error);
        enqueueSnackbar('Failed to download document', { variant: 'error' });
      }
    },
    [fetchDocumentBlob, documentMetrics, enqueueSnackbar],
  );

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedDocumentId(null);
  }, []);

  // Handle pagination model change
  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      const newPage = model.page + 1;
      const newPageSize = model.pageSize;

      if (newPageSize !== pageSize) {
        setPageSize(newPageSize);
      } else if (newPage !== page) {
        setPage(newPage);
      }
      // Track pagination change
      const calculatedTotalPages = totalRows > 0 ? Math.ceil(totalRows / newPageSize) : 0;
      documentMetrics.paginationChanged({
        new_page: newPage,
        rows_per_page: newPageSize,
        total_pages: calculatedTotalPages,
        source: 'labs_page',
      });
    },
    [setPage, setPageSize, page, pageSize, totalRows, documentMetrics],
  );

  // Handle sort model change
  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      if (model.length > 0) {
        const { field, sort } = model[0];
        const fieldMap: Record<string, SortField> = {
          document_title: 'filename',
          filename: 'filename',
          created_at: 'created_at',
        };
        const apiField = fieldMap[field] ?? 'created_at';
        setSortBy(apiField);
        setSortOrder(sort ?? 'desc');
        // Track sort change
        documentMetrics.sortChanged({
          sort_field: apiField,
          sort_order: sort ?? 'desc',
          page_type: 'labs',
        });
      } else {
        setSortBy('created_at');
        setSortOrder('desc');
        documentMetrics.sortChanged({
          sort_field: 'created_at',
          sort_order: 'desc',
          page_type: 'labs',
        });
      }
    },
    [setSortBy, setSortOrder, documentMetrics],
  );

  // Handle patient selection (for form/mobile view)
  const handlePatientSelect = useCallback(
    (patient: Patient | undefined) => {
      const patientId = patient?.patient_id || null;
      setSelectedPatient(patient || null);
      // Sync with filter patient picker
      setPatientId(patientId);
      // Reset form state when patient changes
      setFormData(null);
      setPdfData(null);
      setShowPdfPreview(false);
      // Reset recommendations state
      setShowLabRecommendations(false);
      setPendingRecommendations(null);
      setIsFormDirty(false);
      setCurrentFormValues(null);
      setShouldApplyRecommendations(false);
      // Refresh document list when patient is selected
      if (patient) {
        refresh();
      }
    },
    [refresh, setPatientId],
  );

  // Handle mobile lab order view open
  const handleMobileLabOrderViewOpen = useCallback(() => {
    setIsMobileLabOrderViewOpen(true);
    setSelectedPatient(null);
    // Track mobile view opened
    documentMetrics.mobileViewOpened({ page_type: 'labs' });
    toolbarMetrics.actionClicked({
      action_type: 'order_labs',
      has_patient: false,
    });
  }, [toolbarMetrics, documentMetrics]);

  // Handle mobile lab order view close
  const handleMobileLabOrderViewClose = useCallback(() => {
    // Track mobile view closed
    documentMetrics.mobileViewClosed({
      page_type: 'labs',
      had_patient_selected: !!selectedPatient,
      action_taken: !!pdfData, // If PDF was generated, action was taken
    });
    setIsMobileLabOrderViewOpen(false);
    setSelectedPatient(null);
    // Reset form state
    setFormData(null);
    setPdfData(null);
    setShowPdfPreview(false);
    setShowLabRecommendations(false);
    setPendingRecommendations(null);
    setIsFormDirty(false);
    setCurrentFormValues(null);
    setShouldApplyRecommendations(false);
  }, [documentMetrics, selectedPatient, pdfData]);

  // Handle apply recommendations
  const handleApplyRecommendations = useCallback(() => {
    // Track AI recommendations applied
    if (pendingRecommendations) {
      labOrderMetrics.aiRecommendationsApplied({
        test_count: pendingRecommendations.lab_tests?.length || 0,
        source: 'manual',
      });
    }
    // Set flag to trigger recommendations application
    setShouldApplyRecommendations(true);
    setShowLabRecommendations(false);
  }, [pendingRecommendations, labOrderMetrics]);

  // Handle form submission
  const handleFormSubmit = useCallback(
    async (data: CreateLabOrderRequest) => {
      setFormData(data);

      try {
        const pdfResponse = await generatePdf(data);

        // Track requisition generated
        labOrderMetrics.requisitionGenerated({
          test_count: data.lab_tests?.length || 0,
          has_icd_codes: !!data.icd_10_codes && data.icd_10_codes.length > 0,
        });

        setPdfData(pdfResponse);
        setShowPdfPreview(true);
      } catch (error) {
        enqueueSnackbar('Failed to generate PDF preview', { variant: 'error' });
        console.error('PDF generation error:', error);
      }
    },
    [generatePdf, enqueueSnackbar, labOrderMetrics],
  );

  // Handle send order
  const handleSendOrder = useCallback(async () => {
    if (!formData || !selectedPatient) return;

    // Check if patient has email
    if (!selectedPatient.email) {
      enqueueSnackbar(`${subjectLanguageCap} email is required to send the lab order`, {
        variant: 'warning',
      });
      return;
    }

    createLabOrder(
      { createLabOrderRequest: formData },
      {
        onSuccess: () => {
          labOrderMetrics.sent({
            delivery_method: 'email',
            test_count: formData.lab_tests?.length || 0,
          });

          enqueueSnackbar('Lab order sent successfully!', { variant: 'success' });

          // Reset form and refresh document list
          setFormData(null);
          setPdfData(null);
          setShowPdfPreview(false);
          setSelectedPatient(null);
          refresh();
        },
        onError: error => {
          enqueueSnackbar('Failed to send lab order', { variant: 'error' });
          console.error('Send error:', error);
        },
      },
    );
  }, [
    formData,
    selectedPatient,
    createLabOrder,
    enqueueSnackbar,
    labOrderMetrics,
    subjectLanguageCap,
    refresh,
  ]);

  // Handle back from preview
  const handleBackFromPreview = useCallback(() => {
    setShowPdfPreview(false);
    setPdfData(null);
  }, []);

  // Define columns
  const columns: GridColDef<DocumentSummaryResponse>[] = useMemo(
    () => [
      {
        field: 'document_title',
        headerName: 'Title',
        flex: 2,
        minWidth: 200,
        valueGetter: (value, row) => row.document_title || row.filename,
        renderCell: params => (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ overflow: 'hidden' }}>
            <LabIcon color="info" fontSize="small" />
            <Typography variant="body2" noWrap title={params.value as string}>
              {params.value as string}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'patient',
        headerName: subjectLanguageCap,
        flex: 1.5,
        minWidth: 150,
        sortable: false,
        renderCell: params => {
          const patient = params.row.patient;
          const patientId = params.row.patient_id;

          if (!patient && !patientId) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }

          const firstName = patient?.first_name || '';
          const lastName = patient?.last_name || '';
          const displayName =
            patient?.name ||
            patient?.preferred_name ||
            (firstName && lastName
              ? `${firstName} ${lastName}`
              : firstName || lastName || 'Unknown');

          return (
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ overflow: 'hidden' }}>
              {patientId && (
                <Avatar
                  id={patientId}
                  firstName={firstName || undefined}
                  lastName={lastName || undefined}
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '0.75rem',
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography variant="body2" noWrap title={displayName}>
                {displayName}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'created_at',
        headerName: 'Created',
        width: 130,
        valueFormatter: value => formatDate(value as string),
      },
      {
        field: 'actions',
        headerName: '',
        width: 120,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Download">
              <IconButton
                size="small"
                onClick={e => handleDownloadClick(e, params.row)}
                aria-label="Download document"
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Details">
              <IconButton
                size="small"
                onClick={e => handleViewClick(e, params.row)}
                aria-label="View document details"
              >
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [handleViewClick, handleDownloadClick, subjectLanguageCap],
  );

  // Sort model for DataGrid
  const sortModel: GridSortModel = useMemo(
    () => [{ field: sortBy === 'filename' ? 'document_title' : sortBy, sort: sortOrder }],
    [sortBy, sortOrder],
  );

  // Pagination model
  const paginationModel: GridPaginationModel = useMemo(
    () => ({ page: page - 1, pageSize }),
    [page, pageSize],
  );

  return (
    <Box
      sx={{
        height: { xs: 'calc(100vh - 56px)', md: 'calc(100vh - 64px)' },
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, md: 3 },
        gap: 3,
      }}
    >
      {/* Page Header */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LabIcon color="info" sx={{ fontSize: 32 }} />
              <Typography variant="h4" component="h1" fontWeight={600}>
                Lab Orders
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              View existing lab orders or create new ones
            </Typography>
          </Box>
          {isMobile && !isMobileLabOrderViewOpen && (
            <Button
              variant="contained"
              startIcon={<LabIcon />}
              onClick={handleMobileLabOrderViewOpen}
              sx={{ textTransform: 'none' }}
            >
              Order Labs
            </Button>
          )}
        </Stack>
      </Box>

      {/* Entitlement Usage */}
      {!isMobileLabOrderViewOpen && (
        <EntitlementUsage capabilityName="lab_order_creation" source="labs_page" />
      )}

      {/* Error Alert */}
      {error && !isMobileLabOrderViewOpen && (
        <Box sx={{ flexShrink: 0 }}>
          <Chip label={error} color="error" variant="outlined" />
        </Box>
      )}

      {/* Filters */}
      {!isMobileLabOrderViewOpen && (
        <Box sx={{ flexShrink: 0 }}>
          <DocumentFilters
            filters={filters}
            onPatientIdChange={setPatientId}
            onDocumentTypeChange={() => {}} // Disabled - fixed to lab_order
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onClearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
            isLoading={isLoading || isFetching}
            hideDocumentTypeFilter={true}
          />
        </Box>
      )}

      {isMobileLabOrderViewOpen ? (
        <EntitlementGate
          capabilityName="lab_order_creation"
          source="labs_page_mobile_panel"
          sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          {/* Mobile full-screen lab order view */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            {/* Close button */}
            <IconButton
              onClick={handleMobileLabOrderViewClose}
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                zIndex: 1001,
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': {
                  bgcolor: 'background.paper',
                },
              }}
              aria-label="Close lab order view"
            >
              <CloseIcon />
            </IconButton>

            {/* Header with Patient Picker */}
            <Box
              sx={{
                p: 2,
                flexShrink: 0,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                Create New Lab Order
              </Typography>
              <PatientPicker
                onPatientSelect={handlePatientSelect}
                initialPatient={selectedPatient || undefined}
                size="small"
              />
            </Box>

            {/* Lab Order Form Content */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
              }}
            >
              {!selectedPatient ? (
                // No patient selected - show prompt
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                  }}
                >
                  <LabIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Select a {subjectLanguageCap}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ px: 4 }}>
                    Choose a {subjectLanguageCap.toLowerCase()} above to create a new lab order
                  </Typography>
                </Box>
              ) : showPdfPreview && pdfData ? (
                // PDF Preview
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ mb: 2 }}>
                    <Button startIcon={<BackIcon />} onClick={handleBackFromPreview} size="small">
                      Back to Form
                    </Button>
                  </Box>
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <PdfViewer
                      base64Data={pdfData.pdf_data}
                      filename={pdfData.filename}
                      height="100%"
                      onSendAction={{
                        onClick: handleSendOrder,
                        loading: isCreatingOrder,
                        label: isCreatingOrder
                          ? 'Sending...'
                          : `Send to ${subjectLanguageCap} via Email`,
                      }}
                      eventMetadata={{
                        documentType: 'lab_order',
                        patientId: selectedPatient?.patient_id,
                      }}
                    />
                  </Box>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Click the preview to view the full PDF. Use Print to get a physical copy or
                    Download to save it locally.
                  </Alert>
                </Box>
              ) : (
                <>
                  {/* Lab Recommendations Banner */}
                  {(loadingRecommendations || showLabRecommendations) && (
                    <Alert
                      severity={loadingRecommendations ? 'info' : isFormDirty ? 'success' : 'info'}
                      sx={{ mb: 2 }}
                      icon={loadingRecommendations ? <CircularProgress size={20} /> : undefined}
                      action={
                        !loadingRecommendations && showLabRecommendations ? (
                          isFormDirty && pendingRecommendations ? (
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1}
                              sx={{
                                width: { xs: '100%', sm: 'auto' },
                                mt: { xs: 1, sm: 0 },
                              }}
                            >
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={e => {
                                  setDiffAnchorEl(null);
                                  if (window.matchMedia('(hover: none)').matches) {
                                    setDiffAnchorEl(e.currentTarget);
                                    if (mobileApplyTimeoutRef.current) {
                                      clearTimeout(mobileApplyTimeoutRef.current);
                                    }
                                    mobileApplyTimeoutRef.current = setTimeout(() => {
                                      setDiffAnchorEl(null);
                                      handleApplyRecommendations();
                                    }, 2000);
                                  } else {
                                    handleApplyRecommendations();
                                  }
                                }}
                              >
                                Apply
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setShowLabRecommendations(false);
                                  setPendingRecommendations(null);
                                }}
                              >
                                Dismiss
                              </Button>
                            </Stack>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setShowLabRecommendations(false);
                                setPendingRecommendations(null);
                              }}
                            >
                              Dismiss
                            </Button>
                          )
                        ) : undefined
                      }
                    >
                      {loadingRecommendations
                        ? 'Analyzing patient history for lab recommendations...'
                        : isFormDirty
                          ? 'Lab recommendations available. Review and apply if desired.'
                          : 'AI has suggested lab tests based on patient history. Review and apply if desired.'}
                    </Alert>
                  )}

                  {/* Lab Order Form */}
                  <LabOrderForm
                    onSubmit={handleFormSubmit}
                    isLoading={isGeneratingPdf}
                    patientId={selectedPatient.patient_id}
                    showPatientPicker={false}
                    onDirtyChange={setIsFormDirty}
                    defaultValues={formData || undefined}
                  />
                </>
              )}
            </Box>
          </Box>
        </EntitlementGate>
      ) : (
        <Grid container spacing={3} sx={{ flex: 1, minHeight: 0 }}>
          {/* Left Column - Document List */}
          <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                '& .MuiDataGrid-root': {
                  border: 'none',
                  height: '100%',
                },
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                },
              }}
            >
              <DataGrid
                rows={documents}
                columns={columns}
                getRowId={row => row.document_id}
                loading={isLoading || isFetching}
                pagination
                paginationMode="server"
                rowCount={totalRows}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[10, 20, 50]}
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                onRowClick={handleRowClick}
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                sx={{
                  '& .MuiDataGrid-footerContainer': {
                    minHeight: '52px',
                  },
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none',
                  },
                }}
                slots={{
                  noRowsOverlay: () => (
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      sx={{ height: '100%', py: 4 }}
                    >
                      <LabIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography color="text.secondary">No lab orders found</Typography>
                    </Stack>
                  ),
                }}
              />
            </Box>
          </Grid>

          {/* Right Column - Lab Order Form */}
          <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {!isMobile && (
              <EntitlementGate
                capabilityName="lab_order_creation"
                source="labs_page_panel"
                sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
                overlaySx={{ borderRadius: 2 }}
              >
                <Paper
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  {/* Header with Patient Picker */}
                  <Box
                    sx={{
                      p: 2,
                      borderBottom: 1,
                      borderColor: 'divider',
                      bgcolor: 'grey.50',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                      Create New Lab Order
                    </Typography>
                    <PatientPicker
                      onPatientSelect={handlePatientSelect}
                      initialPatient={selectedPatient || undefined}
                      size="small"
                    />
                  </Box>

                  {/* Content Area */}
                  <Box
                    sx={{
                      flex: 1,
                      overflow: 'auto',
                      p: 2,
                    }}
                  >
                    {!selectedPatient ? (
                      // No patient selected - show prompt
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                        }}
                      >
                        <LabIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          Select a {subjectLanguageCap}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ px: 4 }}>
                          Choose a {subjectLanguageCap.toLowerCase()} above to create a new lab
                          order
                        </Typography>
                      </Box>
                    ) : showPdfPreview && pdfData ? (
                      // PDF Preview
                      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ mb: 2 }}>
                          <Button
                            startIcon={<BackIcon />}
                            onClick={handleBackFromPreview}
                            size="small"
                          >
                            Back to Form
                          </Button>
                        </Box>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                          <PdfViewer
                            base64Data={pdfData.pdf_data}
                            filename={pdfData.filename}
                            height="100%"
                            onSendAction={{
                              onClick: handleSendOrder,
                              loading: isCreatingOrder,
                              label: isCreatingOrder
                                ? 'Sending...'
                                : `Send to ${subjectLanguageCap} via Email`,
                            }}
                            eventMetadata={{
                              documentType: 'lab_order',
                              patientId: selectedPatient?.patient_id,
                            }}
                          />
                        </Box>
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Click the preview to view the full PDF. Use Print to get a physical copy
                          or Download to save it locally.
                        </Alert>
                      </Box>
                    ) : (
                      <>
                        {/* Lab Recommendations Banner */}
                        {(loadingRecommendations || showLabRecommendations) && (
                          <Alert
                            severity={
                              loadingRecommendations ? 'info' : isFormDirty ? 'success' : 'info'
                            }
                            sx={{ mb: 2 }}
                            icon={
                              loadingRecommendations ? <CircularProgress size={20} /> : undefined
                            }
                            action={
                              !loadingRecommendations && showLabRecommendations ? (
                                isFormDirty && pendingRecommendations ? (
                                  <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1}
                                    sx={{
                                      width: { xs: '100%', sm: 'auto' },
                                      mt: { xs: 1, sm: 0 },
                                    }}
                                  >
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="success"
                                      onClick={e => {
                                        // Close any open popover immediately
                                        setDiffAnchorEl(null);

                                        // On mobile, show diff briefly before applying
                                        if (window.matchMedia('(hover: none)').matches) {
                                          setDiffAnchorEl(e.currentTarget);
                                          // Clear any existing timeout
                                          if (mobileApplyTimeoutRef.current) {
                                            clearTimeout(mobileApplyTimeoutRef.current);
                                          }
                                          mobileApplyTimeoutRef.current = setTimeout(() => {
                                            setDiffAnchorEl(null);
                                            handleApplyRecommendations();
                                            mobileApplyTimeoutRef.current = null;
                                          }, 1500);
                                        } else {
                                          // On desktop, apply immediately
                                          handleApplyRecommendations();
                                        }
                                      }}
                                      onMouseEnter={e => {
                                        // Only on desktop with hover support
                                        if (!window.matchMedia('(hover: none)').matches) {
                                          setDiffAnchorEl(e.currentTarget);
                                        }
                                      }}
                                      onMouseLeave={() => {
                                        setDiffAnchorEl(null);
                                      }}
                                      fullWidth
                                      sx={{
                                        minWidth: { xs: '100%', sm: 'auto' },
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      Apply
                                    </Button>
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        setShowLabRecommendations(false);
                                        setPendingRecommendations(null);
                                      }}
                                      fullWidth
                                      sx={{
                                        minWidth: { xs: '100%', sm: 'auto' },
                                      }}
                                    >
                                      Dismiss
                                    </Button>
                                  </Stack>
                                ) : (
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setShowLabRecommendations(false);
                                    }}
                                  >
                                    Dismiss
                                  </Button>
                                )
                              ) : null
                            }
                          >
                            {(() => {
                              if (loadingRecommendations) {
                                return (
                                  <>
                                    <Box
                                      component="span"
                                      sx={{ display: { xs: 'inline', sm: 'none' } }}
                                    >
                                      Analyzing...
                                    </Box>
                                    <Box
                                      component="span"
                                      sx={{ display: { xs: 'none', sm: 'inline' } }}
                                    >
                                      Analyzing for recommendations...
                                    </Box>
                                  </>
                                );
                              }

                              if (
                                labRecommendations?.status === 'completed' &&
                                labRecommendations.lab_order
                              ) {
                                if (isFormDirty) {
                                  return (
                                    <>
                                      <Box
                                        component="span"
                                        sx={{ display: { xs: 'inline', sm: 'none' } }}
                                      >
                                        Recommendations available
                                      </Box>
                                      <Box
                                        component="span"
                                        sx={{ display: { xs: 'none', sm: 'inline' } }}
                                      >
                                        Recommendations available. Click Apply to use lab order
                                        recommendations.
                                      </Box>
                                    </>
                                  );
                                } else {
                                  return (
                                    <>
                                      <Box
                                        component="span"
                                        sx={{ display: { xs: 'inline', sm: 'none' } }}
                                      >
                                        Recommendations applied
                                      </Box>
                                      <Box
                                        component="span"
                                        sx={{ display: { xs: 'none', sm: 'inline' } }}
                                      >
                                        Recommendations applied to the form below
                                      </Box>
                                    </>
                                  );
                                }
                              }

                              return 'No recommendations at this time';
                            })()}
                          </Alert>
                        )}

                        {/* Lab Order Form */}
                        <LabOrderForm
                          patientId={selectedPatient.patient_id}
                          showPatientPicker={false}
                          onSubmit={handleFormSubmit}
                          isLoading={isGeneratingPdf}
                          onDirtyChange={setIsFormDirty}
                          onChange={setCurrentFormValues}
                          pendingRecommendations={pendingRecommendations}
                          shouldApplyRecommendations={shouldApplyRecommendations}
                          onRecommendationsApplied={() => {
                            setShouldApplyRecommendations(false);
                          }}
                          autoApplyRecommendations={
                            !isFormDirty &&
                            labRecommendations?.status === 'completed' &&
                            labRecommendations.lab_order
                              ? labRecommendations.lab_order
                              : false
                          }
                          onPatientSelect={patient => {
                            if (!patient) {
                              setSelectedPatient(null);
                            }
                          }}
                        />
                      </>
                    )}
                  </Box>
                </Paper>
              </EntitlementGate>
            )}
          </Grid>
        </Grid>
      )}

      {/* Document Detail Modal */}
      <DocumentDetailModal
        open={isDetailModalOpen}
        documentId={selectedDocumentId}
        onClose={handleCloseModal}
      />

      {/* Diff Popover for Apply Button */}
      {currentFormValues && pendingRecommendations && (
        <React.Suspense fallback={null}>
          <LabRecommendationsDiff
            anchorEl={diffAnchorEl}
            open={Boolean(diffAnchorEl)}
            onClose={() => {
              setDiffAnchorEl(null);
            }}
            currentValues={{
              lab_tests: currentFormValues.lab_tests || [],
              icd_10_codes: currentFormValues.icd_10_codes || [],
              special_instructions: currentFormValues.special_instructions || '',
            }}
            pendingValues={{
              lab_tests: pendingRecommendations.lab_tests,
              icd_10_codes: pendingRecommendations.icd_10_codes || undefined,
              special_instructions: pendingRecommendations.special_instructions || undefined,
            }}
          />
        </React.Suspense>
      )}
    </Box>
  );
};

export default LabsPage;
